import {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { compareElementsByDocumentOrder } from './compareElementsByDocumentOrder';
import {
  ContentNavigationContext,
  ContentNavigationContextValue,
  ContentNavigationSection,
  RegisteredContentSection,
} from './context';
import { useContentNavigationState } from './useContentNavigationState';

export type ContentNavigationRootProps = PropsWithChildren<{
  currentSection?: string;
  defaultSection?: string;
  onSectionChange?: (id: string) => void;
}>;

export const ContentNavigationProvider = ({
  children,
  currentSection,
  defaultSection,
  onSectionChange,
}: ContentNavigationRootProps): ReactElement => {
  const [sectionsById, setSectionsById] = useState<
    Map<string, RegisteredContentSection>
  >(() => new Map());
  const sections = useMemo(
    () =>
      Array.from(sectionsById)
        .sort(([, firstSection], [, secondSection]) =>
          compareElementsByDocumentOrder(
            firstSection.element,
            secondSection.element,
          ),
        )
        .map(
          ([id, section]): ContentNavigationSection => ({
            id,
            label: section.title,
          }),
        ),
    [sectionsById],
  );
  const {
    currentSection: resolvedCurrentSection,
    goToSection,
    registerSectionElement,
  } = useContentNavigationState({
    currentSection,
    defaultSection: defaultSection ?? sections[0]?.id,
    onSectionChange,
  });
  const registerSection = useCallback(
    (id: string, section: RegisteredContentSection): (() => void) => {
      setSectionsById(currentSections => {
        const existingSection = currentSections.get(id);

        if (existingSection && existingSection.element !== section.element) {
          throw new Error(`Page.ContentSection ids must be unique: "${id}"`);
        }

        if (
          existingSection?.element === section.element &&
          existingSection.title === section.title
        ) {
          return currentSections;
        }

        const nextSections = new Map(currentSections);
        nextSections.set(id, section);
        return nextSections;
      });
      const unregisterSectionElement = registerSectionElement(
        id,
        section.element,
      );

      return () => {
        unregisterSectionElement();
        setSectionsById(currentSections => {
          if (currentSections.get(id)?.element !== section.element) {
            return currentSections;
          }

          const nextSections = new Map(currentSections);
          nextSections.delete(id);
          return nextSections;
        });
      };
    },
    [registerSectionElement],
  );

  const contextValue = useMemo<ContentNavigationContextValue>(
    () => ({
      currentSection: resolvedCurrentSection,
      goToSection,
      registerSection,
      sections,
    }),
    [resolvedCurrentSection, goToSection, registerSection, sections],
  );

  return (
    <ContentNavigationContext.Provider value={contextValue}>
      {children}
    </ContentNavigationContext.Provider>
  );
};
